/**
 * Δ% against the previous period and whether a move is good — one map for the
 * whole page (proposal D1.4), and for D2's tiles too (rules-04 D3). Pure.
 *
 * @module services/looker/lookerDelta
 */
export type MetricDirection = 'up' | 'down' | 'neutral';

/**
 * By bare LookML field name; D2's camelCase tile keys are looked up by the same name (linkClicks → link_clicks).
 * Unlisted = neutral: a colour that might be wrong is worse than none.
 */
const DIRECTION: Readonly<Record<string, MetricDirection>> = {
  ctr: 'up', conversions: 'up', leads: 'up', conversion_value: 'up', roas: 'up', result: 'up',
  cpc: 'down', cpm: 'down', cpl: 'down', cost_per_result: 'down',
  // A link click is a click: it moves as clicks do (on LINE the tile IS the Clicks number).
  spend: 'neutral', impressions: 'neutral', clicks: 'neutral', link_clicks: 'neutral',
  // D2's own tiles (R14, R15). Its costs, like D1's, are better lower.
  reactions: 'up', comments: 'up', shares: 'up', video_views: 'up', view_rate: 'up', engagements: 'up',
  video_completions: 'up', web_conversions: 'up',
  cpv: 'down', cpa: 'down',
  sessions: 'neutral', reach: 'neutral', frequency: 'neutral', engaged_sessions: 'neutral', paid_share: 'neutral',
};

const snakeCase = (key: string): string => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

export const directionOf = (field: string): MetricDirection => DIRECTION[snakeCase(field.split('.').pop() ?? '')] ?? 'neutral';

export const deltaOf = (current: number | null, previous: number | null): number | null =>
  current === null || previous === null || previous === 0 ? null : (current - previous) / Math.abs(previous);

/** On the raw Δ: with the direction is good, against it bad; no Δ, a flat Δ or a neutral metric is neutral. */
export const toneFor = (direction: MetricDirection, delta: number | null): 'good' | 'bad' | 'neutral' => {
  if (delta === null || delta === 0 || direction === 'neutral') return 'neutral';
  return (delta > 0) === (direction === 'up') ? 'good' : 'bad';
};

export const deltaTone = (field: string, delta: number | null): 'good' | 'bad' | 'neutral' => toneFor(directionOf(field), delta);

export const formatDelta = (delta: number): string => `${delta > 0 ? '+' : ''}${(delta * 100).toFixed(1)}%`;

export const TONE_CLASS = { good: 'text-success-700', bad: 'text-error-700', neutral: 'text-neutral-800' } as const;
